import React, {
    useState, useEffect, useRef,
    useImperativeHandle, forwardRef,
} from 'react';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import SignatureInput from './SignatureInput';
import { FormData, ValidationErrors, FormLProps, FormValidateHandle } from './typesIN';

// Location-based POSH chairperson map — update here when names change
const POSH_CHAIRPERSON_BY_LOCATION: Record<string, string> = {
    ahmedabad: 'Navjyot Saini',
    mumbai: 'Sunita Bisht',
    mohali: 'Sunita Bisht',
    hyderabad: 'Neha Pandey',
};

const getPoshChairperson = (locationSite: string): string => {
    if (!locationSite) return '';
    const key = locationSite.toLowerCase();
    // Match if any known city name appears in the location string
    const match = Object.keys(POSH_CHAIRPERSON_BY_LOCATION).find(city => key.includes(city));
    return match ? POSH_CHAIRPERSON_BY_LOCATION[match] : '';
};

const toDateOrUndefined = (v: any): Date | undefined => {
    if (!v) return undefined;
    if (v instanceof Date) return isNaN(v.getTime()) ? undefined : v;
    const d = new Date(String(v));
    return isNaN(d.getTime()) ? undefined : d;
};

const FormL = forwardRef<FormValidateHandle, FormLProps>(
    ({ onSubmit, onDraftSave, onBack, initialData = {}, autoSignature = '' }, ref) => {
        const toast = useRef<Toast>(null);

        const [formData, setFormData] = useState<FormData>({
            ...initialData,
        });
        const [errors, setErrors] = useState<ValidationErrors>({});
        const [acknowledgement, setAcknowledgement] = useState(false);

        // Merge incoming initialData without overwriting existing values
        useEffect(() => {
            if (initialData && Object.keys(initialData).length > 0) {
                setFormData(prev => {
                    const merged = { ...prev };
                    Object.entries(initialData).forEach(([key, val]) => {
                        const existing = prev[key];
                        const isEmpty = existing === undefined || existing === null || existing === '';
                        if (val !== undefined && val !== null && val !== '' && isEmpty) {
                            merged[key] = val;
                        }
                    });
                    merged.poshChairpersonName = getPoshChairperson(merged.workingLocation as string || '');
                    return merged;
                });
            }
        }, [initialData]);

        // Auto-populate employee signature from Form2 subscriber signature
        useEffect(() => {
            if (autoSignature) {
                setFormData(prev => ({ ...prev, employeeSignature: autoSignature }));
            }
        }, [autoSignature]);

        // Draft save with debounce
        const draftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
        useEffect(() => {
            if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
            draftTimerRef.current = setTimeout(() => { onDraftSave?.(formData); }, 500);
            return () => { if (draftTimerRef.current) clearTimeout(draftTimerRef.current); };
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [formData]);

        const handleInputChange = (field: string, value: any) => {
            setFormData(prev => ({ ...prev, [field]: value }));
            if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
        };

        const validateForm = (): boolean => {
            const e: ValidationErrors = {};
            if (!formData.employeeSignature) {
                e.employeeSignature = 'Signature is required';
            }
            if (!acknowledgement) {
                e.acknowledgement = 'Please confirm the acknowledgement';
            }
            setErrors(e);
            const isValid = Object.keys(e).length === 0;
            if (!isValid) {
                toast.current?.show({
                    severity: 'warn',
                    summary: 'Incomplete Form',
                    detail: 'Please sign and confirm the acknowledgement before continuing.',
                    life: 4000,
                });
            }
            return isValid;
        };

        useImperativeHandle(ref, () => ({ validate: validateForm }));

        const handleSubmit = () => {
            if (validateForm()) onSubmit?.(formData);
            else window.scrollTo({ top: 0, behavior: 'smooth' });
        };

        const workingLocation = formData.workingLocation as string || '';
        const chairpersonName = getPoshChairperson(workingLocation);
        const chairpersonDisplay = chairpersonName
            ? `Smt. ${chairpersonName}, TTEC ${workingLocation}`
            : `TTEC ${workingLocation}`;

        return (
            <div className="form-container">
                <Toast ref={toast} position="top-right" />
                <h2 className="form-title">Form L — Consent to Work in Night Shift</h2>
                <p className="form-subtitle">
                    This form confirms your voluntary consent to work in the night shift (9:00 PM – 7:00 AM).
                    All fields below are auto-populated from your profile.
                </p>

                {/* ── Consent narrative ──────────────────────────────────── */}
                <div className="form-l-consent-banner">
                    <p>
                        I <strong>{formData.fullName as string || '___'}</strong> residing at{' '}
                        <strong>{formData.fullAddress as string || '___'}</strong> state that I am working as{' '}
                        <strong>{formData.designation as string || '___'}</strong> in{' '}
                        <strong>M/s. TTEC India Customer Solutions Private Limited</strong> since{' '}
                        <strong>
                            {toDateOrUndefined(formData.dateOfJoining)
                                ? toDateOrUndefined(formData.dateOfJoining)!
                                    .toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })
                                : '___'}
                        </strong>.
                    </p>
                    <p style={{ marginTop: '10px' }}>
                        I am aware that the employer will provide separate safe and secure transport facility from the
                        doorstep of my residence to the place of work and vice-versa, and that there will be at least
                        three women workers working in the night shift, and that there is a Committee to prevent sexual
                        harassment at work place under the Chairmanship of{' '}
                        <strong>{chairpersonDisplay}</strong>.
                    </p>
                    <p style={{ marginTop: '10px' }}>
                        I am therefore willing to work in the night shift for the period from{' '}
                        <strong>9:00 PM</strong> to <strong>7:00 AM</strong>.
                    </p>
                </div>

                {/* ── Row 1: Auto-populated fields ──────────────────────── */}
                <div className="form-row green-bg">
                    <div className="form-field">
                        <label>Full Name</label>
                        <InputText
                            disabled
                            value={formData.fullName as string || ''}
                            placeholder="Auto populated"
                        />
                    </div>
                    <div className="form-field">
                        <label>Designation</label>
                        <InputText
                            disabled
                            value={formData.designation as string || ''}
                            placeholder="Auto populated"
                        />
                    </div>
                    <div className="form-field">
                        <label>Date of Joining <span className="date-hint">(DD/MM/YYYY)</span></label>
                        <Calendar
                            value={toDateOrUndefined(formData.dateOfJoining)}
                            disabled
                            placeholder="Auto populated"
                            showIcon
                            dateFormat="dd/mm/yy"
                            readOnlyInput
                        />
                    </div>
                    <div className="form-field">
                        <label>Working Location</label>
                        <InputText
                            disabled
                            value={formData.workingLocation as string || ''}
                            placeholder="Auto populated"
                        />
                    </div>
                    <div className="form-field">
                        <label>POSH Committee Chairperson</label>
                        <InputText
                            disabled
                            value={chairpersonDisplay}
                            placeholder="Auto populated"
                        />
                    </div>
                </div>

                {/* ── Row 2: Full Address ───────────────────────────────── */}
                <div className="form-row white-bg">
                    <div className="form-field" style={{ gridColumn: 'span 5' }}>
                        <label>Full Address</label>
                        <InputText
                            disabled
                            value={formData.fullAddress as string || ''}
                            placeholder="Auto populated"
                            style={{ width: '100%' }}
                        />
                    </div>
                </div>

                {/* ── Signature & Acknowledgement ───────────────────────── */}
                <div className="section-block">
                    <div className="section-header-bar">
                        <span className="section-header-label">Signature &amp; Acknowledgement</span>
                    </div>

                    <div className="form-row white-bg two-column" style={{ margin: '0 8px' }}>
                        <div className="form-field full-width">
                            <label>
                                Signature of the Women Worker*
                                {autoSignature && formData.employeeSignature === autoSignature && (
                                    <span className="address-copy-badge">auto-filled from Form 2</span>
                                )}
                            </label>
                            <SignatureInput
                                value={formData.employeeSignature as string || ''}
                                onChange={(v) => handleInputChange('employeeSignature', v)}
                                placeholder="Type your full name to sign"
                                className={errors.employeeSignature ? 'p-invalid' : ''}
                            />
                            {errors.employeeSignature && (
                                <small className="p-error">{errors.employeeSignature}</small>
                            )}
                        </div>
                        <div className="form-field full-width">
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <div>
                                    <label>Date <span className="date-hint">(DD/MM/YYYY)</span></label>
                                    <Calendar
                                        value={toDateOrUndefined(formData.dateOfJoining)}
                                        disabled
                                        placeholder="Auto populated"
                                        showIcon
                                        dateFormat="dd/mm/yy"
                                        readOnlyInput
                                    />
                                </div>
                                <div>
                                    <label>Place</label>
                                    <InputText
                                        disabled
                                        value={formData.workingLocation as string || ''}
                                        placeholder="Auto populated"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="acknowledgement-section" style={{ margin: '8px 8px 12px' }}>
                        <div className={`acknowledgement-checkbox-row ${errors.acknowledgement ? 'has-error' : ''}`}>
                            <input
                                type="checkbox"
                                className="ack-checkbox"
                                checked={acknowledgement}
                                onChange={(e) => {
                                    setAcknowledgement(e.target.checked);
                                    if (errors.acknowledgement) setErrors(prev => ({ ...prev, acknowledgement: '' }));
                                }}
                                disabled={!formData.employeeSignature}
                            />
                            <span
                                className="acknowledgement-label"
                                onClick={() => {
                                    if (formData.employeeSignature) setAcknowledgement(!acknowledgement);
                                }}
                            >
                                I hereby certify that the digital signature provided is true, I voluntarily consent to
                                work in the night shift (9:00 PM – 7:00 AM), and the information furnished above is
                                accurate to the best of my knowledge.
                            </span>
                        </div>
                        {!formData.employeeSignature && (
                            <small className="field-hint">Please enter your signature above to enable this checkbox</small>
                        )}
                        {errors.acknowledgement && (
                            <small className="p-error">{errors.acknowledgement}</small>
                        )}
                    </div>
                </div>

                {/* ── Witness Signatures (optional) ─────────────────────── */}
                <div className="section-block">
                    <div className="section-header-bar" style={{ background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)' }}>
                        <span className="section-header-label">Witness Signatures</span>
                    </div>

                    <div className="form-row white-bg two-column" style={{ margin: '0 8px' }}>
                        <div className="form-field full-width">
                            <label>Witness 1 — Signature</label>
                            <InputText
                                disabled
                                value=""
                                placeholder="To be filled by witness"
                            />
                        </div>
                        <div className="form-field full-width">
                            <label>Witness 2 — Signature</label>
                            <InputText
                                disabled
                                value=""
                                placeholder="To be filled by witness"
                            />
                        </div>
                    </div>
                </div>

                <div className="form-actions">
                    <Button
                        label="Back"
                        onClick={onBack}
                        className="back-btn p-button-outlined"
                        style={{ marginRight: 'auto' }}
                    />
                    <Button
                        label="Review and Submit"
                        onClick={handleSubmit}
                        className="continue-btn p-button-primary"
                    />
                </div>
            </div>
        );
    }
);

FormL.displayName = 'FormL';
export default FormL;